import React, { useState, useEffect, useCallback } from 'react';
import { FaBrain, FaRegCheckCircle, FaQuestionCircle } from 'react-icons/fa'; // Added FaQuestionCircle

// --- Game Assets/Configuration ---
const PUZZLE_SIZE = 4; // 4x4 grid
const COLORS = ['#90CAF9', '#A5D6A7', '#FFCC80', '#E6EE9C']; // Calm, pastel colors
const SHAPES = ['circle', 'square', 'triangle', 'star'];

// Generate initial soothing puzzle grid
const generateInitialGrid = () => {
    const tiles = [];
    for (let i = 0; i < PUZZLE_SIZE * PUZZLE_SIZE; i++) {
        tiles.push({
            id: i,
            color: COLORS[i % PUZZLE_SIZE],
            shape: SHAPES[Math.floor(i / PUZZLE_SIZE)],
            correct: false
        });
    }

    // Gentle shuffle
    for (let i = tiles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }

    return tiles;
};

// Check if puzzle is complete
const checkCompletion = (grid) => {
    return grid.every((tile, index) => {
        // Correct position is defined by the index: 
        // Color repeats every PUZZLE_SIZE (0, 1, 2, 3, 0, 1, 2, 3, ...)
        // Shape changes every PUZZLE_SIZE (0, 0, 0, 0, 1, 1, 1, 1, ...)
        return tile.color === COLORS[index % PUZZLE_SIZE] && tile.shape === SHAPES[Math.floor(index / PUZZLE_SIZE)];
    });
};

// --- Personalized Advice & Quotes (Health and Well-Being Focus) ---
const getPersonalizedAdvice = () => {
    const adviceList = [
        "Take a deep breath and feel how calm focus feels — your mind just got a workout. Carry this clarity into your day.",
        "Your ability to stay centered through small challenges builds resilience for life’s bigger moments.",
        "Remember to rest and recharge — balance between action and stillness keeps your energy steady.",
        "Celebrate the small wins, like solving this puzzle. Tiny moments of mindfulness accumulate into lasting well-being.",
        "You gave your brain a peaceful challenge. Try doing the same for your body: stretch, hydrate, and smile."
    ];

    const quotes = [
        "Peace comes from within. Do not seek it without. – Buddha",
        "Almost everything will work again if you unplug it for a few minutes, including you. – Anne Lamott",
        "Calm mind brings inner strength and self-confidence. – Dalai Lama",
        "Self-care is giving the world the best of you, not what’s left of you. – Katie Reed",
        "Take care of your body. It’s the only place you have to live. – Jim Rohn"
    ];

    const advice = adviceList[Math.floor(Math.random() * adviceList.length)];
    const quote = quotes[Math.floor(Math.random() * quotes.length)];

    return { advice, quote };
};

// --- Game Component ---
const FocusFlowGame = () => {
    const [grid, setGrid] = useState(generateInitialGrid());
    const [selectedTileIndex, setSelectedTileIndex] = useState(null);
    const [isSolved, setIsSolved] = useState(false);
    const [result, setResult] = useState(null);
    // ⭐ NEW STATE: To show/hide game instructions
    const [showInstructions, setShowInstructions] = useState(false); 

    const handleTileClick = (index) => {
        if (isSolved) return;

        if (selectedTileIndex === null) {
            // Select the first tile
            setSelectedTileIndex(index);
        } else if (selectedTileIndex === index) {
            // Deselect the currently selected tile
            setSelectedTileIndex(null);
        } else {
            // Swap tiles
            const newGrid = [...grid];
            [newGrid[selectedTileIndex], newGrid[index]] = [newGrid[index], newGrid[selectedTileIndex]];
            setGrid(newGrid);
            setSelectedTileIndex(null);

            if (checkCompletion(newGrid)) {
                setIsSolved(true);
                setResult(getPersonalizedAdvice());
            }
        }
    };

    const resetGame = () => {
        setGrid(generateInitialGrid());
        setIsSolved(false);
        setSelectedTileIndex(null);
        setResult(null);
    };
    
    // ⭐ New Instructions Component (Integrated directly into the main return block)

    if (isSolved && result) {
        return (
            <div className="card challenge-card solved-game-card">
                <h3>🌿 Focus Flow Complete! 🌿</h3>
                <div className="result-content">
                    <h4>Personal Wellness Insight:</h4>
                    <p className="advice-text">{result.advice}</p>
                    <hr />
                    <h4>Daily Inspiration:</h4>
                    <p className="quote-text">"{result.quote}"</p>
                </div>
                <button className="challenge-button" onClick={resetGame}>
                    <FaRegCheckCircle /> Play Again
                </button>
            </div>
        );
    }

    return (
        <div className="card challenge-card focus-flow-card">
            <h3><FaBrain /> Focus Flow Game</h3>
            <p className="challenge-text">Relax and refocus by arranging the tiles into harmonious order. Breathe, align, and restore balance.</p>

            {/* ⭐ INSTRUCTIONS TOGGLE BUTTON */}
            <button 
                className="challenge-button instruction-button" 
                onClick={() => setShowInstructions(!showInstructions)}
            >
                <FaQuestionCircle /> {showInstructions ? 'Hide Instructions' : 'How to Play'}
            </button>
            
            {/* ⭐ INSTRUCTIONS TEXT BLOCK (Conditionally rendered) */}
            {showInstructions && (
                <div className="instructions-panel card full-width">
                    <h4>The Goal:</h4>
                    <p>Arrange the tiles so that all four colors and all four shapes are aligned into a perfect 4x4 pattern.</p>
                    <p>When solved, the grid will show a repeating pattern of colors across the rows, and a repeating pattern of shapes down the columns (or vice-versa, depending on the generation logic).</p>
                    
                    <h4>How to Play:</h4>
                    <ol>
                        <li>Select: Click on the first tile you want to move. It will be highlighted.</li>
                        <li>Swap: Click on the second tile (the one you want to swap with).</li>
                        <li>The two tiles will swap positions. Repeat this process until the grid is solved.</li>
                    </ol>
                    <p>A successful pattern will have a clear, repeating sequence of colors and shapes.</p>
                </div>
            )}
            {/* --- END INSTRUCTIONS --- */}

            <div
                className="focus-flow-grid"
                style={{
                    '--grid-size': PUZZLE_SIZE,
                    gridTemplateColumns: `repeat(${PUZZLE_SIZE}, 1fr)`
                }}
            >
                {grid.map((tile, index) => (
                    <div
                        key={tile.id}
                        className={`flow-tile ${selectedTileIndex === index ? 'selected' : ''}`}
                        style={{ backgroundColor: tile.color }}
                        onClick={() => handleTileClick(index)}
                    >
                        <div className={`tile-shape ${tile.shape}`}></div>
                    </div>
                ))}
            </div>

            <button className="challenge-button reset-button" onClick={resetGame}>
                Reset Puzzle
            </button>
        </div>
    );
};

export default FocusFlowGame;